import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import type { CampRegistrationListItemDTO } from "../api/client";
import {
  CAMP_REGISTRATION_STATUS_LABELS,
  CAMP_REGISTRATION_STATUS_VARIANTS,
} from "../lib/camp-status";

type CampRegistrationsTableProps = {
  isLoading: boolean;
  data: CampRegistrationListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (id: string) => void;
};

export function CampRegistrationsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
  onView,
}: CampRegistrationsTableProps) {
  const columns: TanstackReactTable.ColumnDef<CampRegistrationListItemDTO>[] = [
    createSortableColumn<CampRegistrationListItemDTO>("submittedAt", "ວັນທີສະໝັກ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.submittedAt)}
        </span>
      ),
    }),
    createSortableColumn<CampRegistrationListItemDTO>("fullName", "ຜູ້ສະໝັກ", {
      size: 180,
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.fullName}</p>
          <p className="text-muted-foreground text-xs">{row.original.nickname}</p>
        </div>
      ),
    }),
    createSortableColumn<CampRegistrationListItemDTO>("phoneNumber", "ເບີໂທ", {
      size: 130,
      cell: ({ row }) => <span>{row.original.phoneNumber}</span>,
    }),
    {
      id: "classes",
      header: "ຄລາສ",
      size: 220,
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.classes.map((item) => (
            <Badge key={item.classTypeId} variant="secondary" className="text-xs">
              {item.name}
            </Badge>
          ))}
        </div>
      ),
    },
    createSortableColumn<CampRegistrationListItemDTO>("status", "ສະຖານະ", {
      size: 140,
      cell: ({ row }) => (
        <Badge
          variant={
            CAMP_REGISTRATION_STATUS_VARIANTS[row.original.status] ?? "outline"
          }
        >
          {CAMP_REGISTRATION_STATUS_LABELS[row.original.status] ??
            row.original.status}
        </Badge>
      ),
    }),
    {
      id: "actions",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="size-4" />,
              onClick: () => onView(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<CampRegistrationListItemDTO, unknown>
      noDataMessage="ບໍ່ພົບໃບສະໝັກ Camp"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting || sorting.length === 0) return;
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
      }}
    />
  );
}
